import React, { useState, useEffect, useMemo } from 'react';

import { format, parseISO } from 'date-fns';

import request from '@nineeng/request';

import * as authentication from '../util/authentication.js';
import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import ContentContainer from '../layout/ContentContainer.jsx';
import Checkbox from '../components/Checkbox.jsx';
import MultipleFileUpload from '../components/MultipleFileUpload.jsx';
import MUICheckbox from '@mui/material/Checkbox';

import Card from '@mui/material/Card';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';

import {
	getSatisfiedDocumentTypes,
	flattenRequirementTypes,
	isRequirementGroupSatisfied,
	getUnsatisfiedRequirements,
} from '../util/safetyCertificates.js';

import { getTextDefaults } from './translations/safetyCertificatesTranslations';

// Generic type to allow recognition when document is clearly a user safety cert but not a specific type
const GENERIC_USER_SAFETY_CERTIFICATE_TYPE = 'user-safety-certificate';

// Stable empty array so memoized values don't create a new [] every render when empty
const EMPTY_ARRAY = [];

// Helper to get display name from document types
const getDisplayName = (typeId, documentTypesMap = {}) => {
	const docType = documentTypesMap[typeId];
	return docType?.name || typeId;
};

// Normalize to list of requirement groups: [['id1'], ['id2', 'id3']]
// Supports backward compat: flat ['id1', 'id2'] → [['id1'], ['id2']]
const normalizeToRequirementGroups = (idsOrGroups) => {
	if (!idsOrGroups || idsOrGroups.length === 0) return [];
	return idsOrGroups.map((item) => (Array.isArray(item) ? item : [item]));
};

// Questions Page Component - Yes/No button-style radio buttons
const QuestionsPage = ({ workActivities = [], answers = {}, onAnswerChange, text, disabled, title, subtitle }) => {
	const handleButtonClick = (activityLabel, value) => {
		if (disabled) return;
		onAnswerChange({
			target: {
				name: activityLabel,
				value: value,
			},
		});
	};

	return (
		<ContentContainer>
			<Typography variant="h6" sx={{ marginBottom: 2 }}>
				{title || 'Work Activity Questions'}
			</Typography>
			<Typography variant="body2" sx={{ marginBottom: 3, color: 'text.secondary' }}>
				{subtitle ||
					'Please answer the following questions about the type of work you will be performing.'}
			</Typography>
			{workActivities.map((activity, index) => {
				const isYesSelected = answers[activity.label] === true;
				const isNoSelected = answers[activity.label] === false;

				return (
					<Box key={activity.label || index} sx={{ marginBottom: 4 }}>
						<Typography variant="body1" sx={{ marginBottom: 2, fontWeight: 500 }}>
							{activity.questionText || activity.label}
						</Typography>
						<Box sx={{ display: 'flex', gap: 0, width: '100%' }}>
							<Button
								onClick={() => handleButtonClick(activity.label, 'yes')}
								disabled={disabled}
								variant="outlined"
								sx={{
									flex: 1,
									borderRadius: '8px 0 0 8px',
									padding: '12px 24px',
									fontSize: '1rem',
									fontWeight: 500,
									textTransform: 'none',
									backgroundColor: isYesSelected ? 'success.main' : 'transparent', // Brand green
									color: isYesSelected ? 'white' : 'text.primary',
									borderColor: isYesSelected ? 'success.main' : 'grey.300',
									borderRight: 'none',
									'&:hover': {
										backgroundColor: isYesSelected ? 'success.main' : 'transparent',
										borderColor: isYesSelected ? 'success.dark' : 'success.main',
										borderRight: 'none',
									},
									'&.Mui-disabled': {
										borderColor: 'grey.300',
										borderRight: 'none',
									},
								}}
							>
								{text.yesLabel || 'Yes'}
							</Button>
							<Button
								onClick={() => handleButtonClick(activity.label, 'no')}
								disabled={disabled}
								variant="outlined"
								sx={{
									flex: 1,
									borderRadius: '0 8px 8px 0',
									padding: '12px 24px',
									fontSize: '1rem',
									fontWeight: 500,
									textTransform: 'none',
									backgroundColor: isNoSelected ? 'grey.800' : 'transparent', // Dark gray
									color: isNoSelected ? 'white' : 'text.primary',
									borderColor: isNoSelected ? 'grey.800' : 'grey.300',
									'&:hover': {
										backgroundColor: isNoSelected ? 'grey.800' : 'transparent',
										borderColor: isNoSelected ? 'grey.900' : 'primary.main',
									},
									'&.Mui-disabled': {
										borderColor: 'grey.300',
									},
								}}
							>
								{text.noLabel || 'No'}
							</Button>
						</Box>
					</Box>
				);
			})}
		</ContentContainer>
	);
};

// Processing Page Component
const ProcessingPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Box sx={{ textAlign: 'center' }}>
				<CircularProgress sx={{ marginBottom: 2 }} />
				<Typography>{text.processingText || 'Searching for certificates...'}</Typography>
			</Box>
		</WelcomeScreen>
	);
};

// Upload Page Component with visual status of existing documents
const UploadPage = ({
	text,
	userDocuments = [],
	existingUserDocuments = [], // Documents already in user's account
	userCertificates = [], // Certificates found from external sources
	onCertificateToggle,
	onDocumentChange,
	onMissingCertificationsAcknowledgment,
	missingCertificationsAcknowledged,
	disabled,
	uploadUrl,
	input,
	useCamera,
	cameraIncludes,
	cameraRotationAngle,
	requiredDocumentTypes = [], // Document type IDs required based on "yes" answers
	documentTypesMap = {}, // Map of documentTypeId -> DocumentType object
}) => {
	// Combine existing user documents with newly uploaded documents
	// Also include selected certificates from external sources
	const selectedCertificates = userCertificates.filter((cert) => cert.selected !== false);
	const allDocuments = [...existingUserDocuments, ...userDocuments];
	const satisfiedTypes = getSatisfiedDocumentTypes('user', selectedCertificates, allDocuments);

	// All document types are treated as required (but skippable with checkbox)
	// requiredDocumentTypes is a list of requirement groups: [['id1'], ['id2', 'id3']]
	// Single-item group: one document type required. Multi-item: only one of the group needed (OR logic)
	const safeRequiredDocumentTypes = requiredDocumentTypes || [];
	const allRequiredTypes = normalizeToRequirementGroups(safeRequiredDocumentTypes);

	const stillNeededRequired = getUnsatisfiedRequirements(allRequiredTypes, satisfiedTypes);

	// Helper to get existing documents that satisfy a requirement
	const getExistingDocumentsForType = (docType) => {
		return allDocuments.filter((doc) => {
			if (doc.validity === false) return false; // Exclude invalid documents
			const docSatisfiedTypes = getSatisfiedDocumentTypes('user', [], [doc]);
			return docSatisfiedTypes.includes(docType);
		});
	};

	// Group requirements into satisfied and unsatisfied
	const satisfiedRequirements = allRequiredTypes.filter((reqGroup) =>
		isRequirementGroupSatisfied(reqGroup, satisfiedTypes),
	);
	const unsatisfiedRequirements = allRequiredTypes.filter(
		(reqGroup) => !isRequirementGroupSatisfied(reqGroup, satisfiedTypes),
	);

	// Helper to get expiry date from document or certificate
	const getExpiryDate = (docType) => {
		// First check if there's a selected certificate that satisfies this type
		const matchingCert = selectedCertificates.find((cert) => {
			const certTypes = getSatisfiedDocumentTypes('user', [cert], []);
			return certTypes.includes(docType);
		});
		if (matchingCert?.validUntil) {
			return matchingCert.validUntil;
		}

		// Then check documents
		const matchingDocs = getExistingDocumentsForType(docType);
		for (const doc of matchingDocs) {
			const expiry = doc.validUntil || doc.metadata?.expiryDate || doc.expiryDate;
			if (expiry) return expiry;
		}
		return null;
	};

	// Format expiry date
	const formatExpiryDate = (date) => {
		if (!date) return null;
		const d = new Date(date);
		return d.toLocaleDateString('en-CA'); // YYYY-MM-DD format
	};

	// status: 'verified' | 'missing' | 'inactive' (inactive = part of a satisfied group but not the one that satisfied it)
	const renderDocumentCard = (typeId, status = 'missing', marginBottom = 2) => {
		const isVerified = status === 'verified';
		const isInactive = status === 'inactive';
		const expiryDate = isVerified ? getExpiryDate(typeId) : null;
		return (
			<Card
				key={typeId}
				variant="outlined"
				sx={{
					marginBottom,
					borderRadius: 1,
					borderColor: 'grey.300',
					backgroundColor: 'white',
					opacity: isInactive ? 0.5 : 1,
				}}
			>
				<ListItem>
					<ListItemIcon sx={{ minWidth: 48 }}>
						{isVerified ? (
							<CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
						) : (
							<ErrorOutlineIcon sx={{ color: 'grey.600', fontSize: 28 }} />
						)}
					</ListItemIcon>
					<ListItemText
						primary={
							<Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
								{getDisplayName(typeId, documentTypesMap)}
							</Typography>
						}
						secondary={
							isInactive ? null : (
								<Typography
									variant="caption"
									sx={{ color: isVerified ? 'success.main' : 'warning.main', fontWeight: 500 }}
								>
									{isVerified
										? `${text.verifiedLabel || 'VERIFIED'}${expiryDate ? ` (${text.expiresLabel || 'EXPIRES'} ${formatExpiryDate(expiryDate)})` : ''}`
										: text.actionRequiredLabel || 'ACTION REQUIRED'}
								</Typography>
							)
						}
					/>
				</ListItem>
			</Card>
		);
	};

	// Shared renderer for multi-item group box (dotted border + "one of the following" label)
	const renderGroupBox = (requirementGroup, getStatus) => {
		const types = flattenRequirementTypes([requirementGroup]);
		return (
			<Box key={JSON.stringify(requirementGroup)} sx={{ marginBottom: 2 }}>
				<Typography variant="body2" sx={{ marginBottom: 1, color: 'text.secondary' }}>
					{text.oneOfFollowingLabel || 'Upload at least one of the following:'}
				</Typography>
				<Box sx={{ border: '2px dashed', borderColor: 'grey.400', borderRadius: 1, padding: 2 }}>
					{types.map((typeId, idx) => renderDocumentCard(typeId, getStatus(typeId), idx === types.length - 1 ? 0 : 2))}
				</Box>
			</Box>
		);
	};

	// Render verified document (satisfied requirement)
	const renderVerifiedDocument = (requirementGroup) => {
		const flattenedTypes = flattenRequirementTypes([requirementGroup]);
		if (flattenedTypes.length > 1) {
			return renderGroupBox(requirementGroup, (typeId) => (satisfiedTypes.includes(typeId) ? 'verified' : 'inactive'));
		}
		const satisfiedType = flattenedTypes.find((type) => satisfiedTypes.includes(type));
		if (!satisfiedType) return null;
		return renderDocumentCard(satisfiedType, 'verified', 2);
	};

	// Render missing document (unsatisfied requirement)
	const renderMissingDocument = (requirementGroup) => {
		const types = flattenRequirementTypes([requirementGroup]);
		if (types.length > 1) {
			return renderGroupBox(requirementGroup, () => 'missing');
		}
		return renderDocumentCard(types[0], 'missing', 2);
	};

	const total = allRequiredTypes.length;
	const missing = unsatisfiedRequirements.length;
	const totalText = total === 1
		? `1 ${text.certificateSingular || 'certificate'}`
		: `${total} ${text.certificatePlural || 'certificates'}`;
	const missingText = missing === 1
		? `1 ${text.missingCountSingular || 'is'}`
		: `${missing} ${text.missingCountPlural || 'are'}`;
	const summaryText =
		missing === 0
			? text.summaryTextAllComplete || 'All required certificates are available.'
			: (text.summaryText || `You need ${totalText}. ${missingText} currently missing from your profile.`)
					.replace('{total}', totalText)
					.replace('{missing}', missingText);

	return (
		<ContentContainer>
			<Typography variant="h5" sx={{ marginBottom: 1 }}>
				{text.uploadTitle || 'Validation & Gap Analysis'}
			</Typography>

			{/* Summary Banner */}
			{allRequiredTypes.length > 0 && (
				<Box
					sx={{
						marginBottom: 3,
						padding: 3,
						borderRadius: 1,
						backgroundColor: 'grey.100',
						display: 'flex',
						alignItems: 'center',
						gap: 2,
					}}
				>
					<Box
						sx={{
							width: 48,
							height: 48,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
						}}
					>
						{unsatisfiedRequirements.length === 0 ? (
							<CheckCircleIcon sx={{ color: 'success.main', fontSize: 40 }} />
						) : (
							<ErrorOutlineIcon sx={{ color: 'grey.600', fontSize: 40 }} />
						)}
					</Box>
					<Box sx={{ flex: 1 }}>
						<Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, marginBottom: 0.5 }}>
							{text.analysisCompleteLabel || 'Analysis Complete'}
						</Typography>
						<Typography variant="body2" sx={{ color: 'text.primary' }}>
							{summaryText}
						</Typography>
					</Box>
				</Box>
			)}

			{/* Verified Documents (Satisfied) */}
			{satisfiedRequirements.length > 0 && (
				<Box sx={{ width: '100%' }}>
					{satisfiedRequirements.map((requirementGroup, index) => (
						<Box
							key={JSON.stringify(requirementGroup)}
							sx={{
								marginBottom: index === satisfiedRequirements.length - 1 && unsatisfiedRequirements.length > 0 ? 0 : 2,
							}}
						>
							{renderVerifiedDocument(requirementGroup)}
						</Box>
					))}
				</Box>
			)}

			{/* Missing Documents (Unsatisfied) */}
			<Box sx={{ marginBottom: 4, width: '100%' }}>
				{unsatisfiedRequirements.map((requirementGroup) => renderMissingDocument(requirementGroup))}
			</Box>

			{/* Found Certificates (below analysis summary and document status, above upload) */}
			{userCertificates.length > 0 && (
				<Box sx={{ marginBottom: 4, width: '100%' }}>
					<Typography variant="h6" sx={{ marginBottom: 1 }}>
						{text.foundCertificatesTitle || 'Found Certificates'}
					</Typography>
					<Typography variant="body2" sx={{ marginBottom: 2, color: 'text.secondary' }}>
						{text.foundCertificatesSubtitle ||
							'We found the following certificates. Please deselect any that are not relevant:'}
					</Typography>
					{userCertificates.map((cert, index) => (
						<Card
							key={cert.certificateNumber || cert.id || index}
							variant="outlined"
							sx={{ '& p': { margin: 0 }, marginBottom: 2, width: '100%', borderRadius: 1 }}
						>
							<ListItem sx={{ height: 100 }} disabled={disabled}>
								<ListItemIcon sx={{ minWidth: 80 }}>
									<Checkbox
										value={cert.selected !== false}
										onChange={(e) => onCertificateToggle(index, e.target.value)}
										disabled={disabled}
									/>
								</ListItemIcon>
								<ListItemText
									primary={cert.certificateType || cert.type}
									primaryTypographyProps={{ color: 'green', noWrap: true }}
									secondary={
										<>
											{cert.certificateNumber && (
												<Typography>
													{text.certificateNumber || 'Certificate Number'}: {cert.certificateNumber}
												</Typography>
											)}
											{cert.certificateIssuer && (
												<Typography variant="caption">
													{text.certificateIssuer || 'Issuer'}: {cert.certificateIssuer}
												</Typography>
											)}
											{cert.validUntil && (
												<Typography variant="caption" sx={{ marginLeft: 1 }}>
													{text.validUntil || 'Valid Until'}: {new Date(cert.validUntil).toLocaleDateString()}
												</Typography>
											)}
										</>
									}
								/>
							</ListItem>
						</Card>
					))}
				</Box>
			)}

			{/* Upload Zone - Single dropbox for all documents */}
			{allRequiredTypes.length > 0 && (
				<Box sx={{ marginBottom: 4, width: '100%' }}>
					<Typography variant="h6" sx={{ marginBottom: 1 }}>
						{text.uploadDocumentsTitle || 'Upload Documents'}
					</Typography>
					<Typography variant="body2" sx={{ marginBottom: 2, color: 'text.secondary' }}>
						{text.uploadDocumentsSubtitle ||
							'Drag and drop files here or click to select. The system will automatically detect the document type.'}
					</Typography>
					<MultipleFileUpload
						onFilesUploaded={onDocumentChange}
						uploadUrl={uploadUrl}
						disabled={disabled}
						text={text}
						existingFiles={userDocuments}
						fileType="unknown-user-document-type"
						allowedDocumentTypes={[
							...new Set([...flattenRequirementTypes(stillNeededRequired), GENERIC_USER_SAFETY_CERTIFICATE_TYPE]),
						]}
						showAiProcessingState
						documentTypesMap={documentTypesMap}
						context={{
							expectedFile: 'safety or training related certificate of a person (not a company).',
							userData: input,
						}}
						requiredDocumentTypes={allRequiredTypes}
						getSatisfiedTypes={(certType) =>
							getSatisfiedDocumentTypes('user', [{ type: certType, selected: true }], [])
						}
						useCamera={useCamera}
						cameraIncludes={cameraIncludes}
						cameraRotationAngle={cameraRotationAngle}
					/>
				</Box>
			)}

			{/* No Documents Needed Confirmation */}
			{allRequiredTypes.length === 0 && (
				<Box
					sx={{
						marginBottom: 3,
						padding: 3,
						borderRadius: 1,
						backgroundColor: 'success.light',
						display: 'flex',
						alignItems: 'center',
						gap: 2,
					}}
				>
					<Box
						sx={{
							width: 48,
							height: 48,
							borderRadius: '50%',
							backgroundColor: 'success.main',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
						}}
					>
						<CheckCircleIcon sx={{ color: 'white', fontSize: 28 }} />
					</Box>
					<Box sx={{ flex: 1 }}>
						<Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, marginBottom: 0.5 }}>
							{text.noDocumentsNeededTitle || 'No Documents Required'}
						</Typography>
						<Typography variant="body2" sx={{ color: 'text.primary' }}>
							{text.noDocumentsNeededMessage ||
								'Based on your answers, no additional safety certificates are required for this work type.'}
						</Typography>
					</Box>
				</Box>
			)}

			{/* Missing Certifications Acknowledgment Checkbox - Show when required documents are not all satisfied */}
			{allRequiredTypes.length > 0 && stillNeededRequired.length > 0 && (
				<Box
					sx={{
						marginBottom: 2,
						marginTop: 2,
						padding: 2,
						borderRadius: 1,
						backgroundColor: 'grey.100',
					}}
				>
					<FormControlLabel
						control={
							<MUICheckbox
								checked={missingCertificationsAcknowledged}
								onChange={(e) => onMissingCertificationsAcknowledgment(e.target.checked)}
								disabled={disabled}
							/>
						}
						label={
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<ErrorOutlineIcon sx={{ color: 'grey.600', fontSize: 24, flexShrink: 0 }} />
								<Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
									&ldquo;
									{text.missingCertificationsAcknowledgment ||
										'I acknowledge the missing certifications and confirm I have valid temporary paper copies to present on-site.'}
									&rdquo;
								</Typography>
							</Box>
						}
					/>
				</Box>
			)}
		</ContentContainer>
	);
};

// Main Component
const WorkTypeSafetyCertificatesSection = ({
	onChange = () => {},
	values = {},
	textOverride = {},
	background = 'light',
	navigationProps,
	selectedLanguage = 'EN',
	uploadUrl = '/api/files',
	disabled = false,
	useCamera = true,
	cameraIncludes = '',
	style = {},
	cameraRotationAngle = 0,
	fields = [],
	workActivities: workActivitiesProp = [],
	apiUrl = '/api/validate/certificates',
	documentTypesUrl,
	title,
	subtitle,
}) => {
	const workActivitiesKey = useMemo(
		() =>
			JSON.stringify(
				(fields || [])
					.filter((f) => f.type === 'WorkActivityQuestion')
					.map((f) => [f.label, f.questionText, f.requiredDocumentTypeIds]),
			),
		[fields],
	);
	const activitiesFromFields = useMemo(
		() =>
			(fields || [])
				.filter((f) => f.type === 'WorkActivityQuestion')
				.map((f) => ({
					label: f.label,
					questionText: f.questionText,
					requiredDocumentTypeIds: f.requiredDocumentTypeIds,
				})),
		// Depend on serialized content, not array reference, because the Form renderer
		// creates new field arrays on every render when disabled={true} (via disableFields).
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[workActivitiesKey],
	);
	const workActivities = activitiesFromFields.length > 0 ? activitiesFromFields : workActivitiesProp;
	const text = Object.assign({}, getTextDefaults(selectedLanguage), textOverride);

	const inputData = values.input || {};
	const userData = values.userData || {}; // values coming from refDataMap (e.g. 'userData.userId': 'user.id')
	const input = {
		...userData,
		...inputData,
	};

	// User ID for fetching existing files; from refDataMap (userData.userId -> user.id)
	const userId = input.userId;

	// Fetched user documents (used for satisfaction only; not listed in UI).
	const [fetchedUserDocuments, setFetchedUserDocuments] = useState([]);
	// Stable reference when empty so children don't get a new [] every render
	const existingUserDocuments = useMemo(
		() => (fetchedUserDocuments.length > 0 ? fetchedUserDocuments : EMPTY_ARRAY),
		[fetchedUserDocuments],
	);

	const answers = values.answers || {};
	// Stable reference when empty so MultipleFileUpload's ref isn't overwritten with a new [] every render
	const userDocuments = useMemo(() => values.userDocuments ?? EMPTY_ARRAY, [values.userDocuments]);
	const missingCertificationsAcknowledged = values.missingCertificationsAcknowledged || false;

	// Local state: Store ALL certificates when fetched (for order and full list)
	const [allCertificates, setAllCertificates] = useState([]);
	// Selected certificates from parent only (same pattern as SafetyCertificatesUserSection)
	const selectedCertificates = values.userCertificates || [];

	// Fetched document types map (for display names, externalSources). Not needed on first (questions) page; fetched on init so ready by upload page.
	const [fetchedDocumentTypesMap, setFetchedDocumentTypesMap] = useState({});

	async function fetchDocumentTypesMap() {
		const url = documentTypesUrl ?? '/api/documentTypes?noPagination=true&model=users';
		const authToken = authentication.extractAuthorization();
		const authParams = authentication.extractAuthParams();
		const { body } = await request.get(`${url}&${authParams}`, { headers: { Authorization: authToken } });
		const list = body?.data ?? (Array.isArray(body) ? body : []);
		const map = {};
		(list || []).forEach((dt) => {
			const id = dt.id ?? dt._id;
			if (id) map[id] = dt;
		});
		return map;
	}

	useEffect(() => {
		const ids = new Set();
		workActivities.forEach((activity) => {
			(activity.requiredDocumentTypeIds || []).forEach((item) => {
				if (Array.isArray(item)) {
					item.forEach((id) => ids.add(id));
				} else {
					ids.add(item);
				}
			});
		});
		if (ids.size === 0) return;
		let cancelled = false;
		fetchDocumentTypesMap()
			.then((map) => {
				if (!cancelled) setFetchedDocumentTypesMap(map);
			})
			.catch(() => {
				if (!cancelled) setFetchedDocumentTypesMap({});
			});
		return () => {
			cancelled = true;
		};
	}, [workActivities, documentTypesUrl]);

	async function fetchUserDocuments(userIdForFetch) {
		const url = `/api/users/${userIdForFetch}/files`;
		const { body } = await request.get(url);
		const list = body?.data ?? (Array.isArray(body) ? body : []);
		return list || [];
	}

	// Fetch user documents once when userId is available (effect runs only when userId changes)
	useEffect(() => {
		if (!userId) return;
		let cancelled = false;
		fetchUserDocuments(userId)
			.then((list) => {
				if (!cancelled) setFetchedUserDocuments(list);
			})
			.catch(() => {
				if (!cancelled) setFetchedUserDocuments([]);
			});
		return () => {
			cancelled = true;
		};
	}, [userId]);

	// When cert list is cleared (e.g. no BeSaCC or left flow), clear form value
	useEffect(() => {
		if (allCertificates.length === 0 && (values.userCertificates?.length ?? 0) > 0) {
			onChange({ target: { name: 'userCertificates', value: [] } });
		}
	}, [allCertificates.length, values.userCertificates?.length, onChange]);

	// Same key shape as SafetyCertificatesUserSection (certificateNumber, certificateType, certificateIssuer)
	const getCertKey = (cert) =>
		`${cert.certificateNumber ?? cert.id ?? ''}-${cert.certificateType ?? cert.type ?? ''}-${cert.certificateIssuer ?? ''}`;

	const selectedByKey = useMemo(() => {
		const map = {};
		selectedCertificates.forEach((cert) => {
			map[getCertKey(cert)] = cert;
		});
		return map;
	}, [selectedCertificates]);

	// Combine: iterate through all certificates, mark as selected if in parent
	const userCertificates = allCertificates.map((cert) => ({
		...cert,
		selected: !!selectedByKey[getCertKey(cert)],
	}));

	// Memoize so the existingMatchingDocuments effect doesn't run on every render (avoids double onChange with certificate toggle)
	// requiredDocumentTypes is a list of requirement groups: [['id1'], ['id2', 'id3']]
	// Single-item group: one document type required. Multi-item: only one of the group needed (OR logic)
	// Single-file requirements are deduplicated (same doc type in multiple activities shown once)
	const requiredDocumentTypes = useMemo(() => {
		const groups = [];
		const seenSingleIds = new Set();
		workActivities.forEach((activity) => {
			if (answers[activity.label] !== true) return;

			if (activity.requiredDocumentTypeIds?.length > 0) {
				activity.requiredDocumentTypeIds.forEach((item) => {
					if (Array.isArray(item)) {
						if (item.length === 1) {
							if (!seenSingleIds.has(item[0])) {
								seenSingleIds.add(item[0]);
								groups.push(item);
							}
						} else {
							groups.push(item);
						}
					} else {
						if (!seenSingleIds.has(item)) {
							seenSingleIds.add(item);
							groups.push([item]);
						}
					}
				});
			}
		});
		return groups;
		// Stable deps: only recompute when yes/no answers or workActivities actually change
	}, [workActivities, workActivities.map((a) => answers[a.label]).join(',')]);

	// Document types map is always the fetched one (from documentTypesUrl on init).
	const documentTypesMap = fetchedDocumentTypesMap;

	// Check if any required document type has BeSaCC external source (document type API: externalSources[].source is string e.g. "BeSaCC")
	const needsBeSaCCLookup = () => {
		const allIds = flattenRequirementTypes(requiredDocumentTypes);
		return allIds.some((docTypeId) => {
			const docType = documentTypesMap[docTypeId];
			return docType?.externalSources?.some((source) => source?.source === 'BeSaCC') === true;
		});
	};

	const handleCanContinue = () => {
		// Combine existing and new documents, plus selected certificates
		const selectedCerts = userCertificates.filter((c) => c.selected !== false);
		const allDocuments = [...existingUserDocuments, ...userDocuments];

		// requiredDocumentTypes is already list of groups (from useMemo)
		if (requiredDocumentTypes.length === 0) {
			return true;
		}

		const satisfiedTypes = getSatisfiedDocumentTypes('user', selectedCerts, allDocuments);
		const allRequiredSatisfied = requiredDocumentTypes.every((requirementGroup) =>
			isRequirementGroupSatisfied(requirementGroup, satisfiedTypes),
		);

		return allRequiredSatisfied || missingCertificationsAcknowledged;
	};

	// Pass full existing (already-on-account) documents that satisfy one of the required types; newly uploaded docs are in userDocuments at submit.
	// Intentionally omit onChange from deps to avoid effect running on every parent re-render (onChange reference changes often).
	useEffect(() => {
		const existingDocs = existingUserDocuments.filter((d) => d.validity !== false);
		const allRequiredIds = flattenRequirementTypes(requiredDocumentTypes);
		const matchedDocs =
			allRequiredIds.length === 0
				? []
				: existingDocs.filter((doc) => {
						const docSatisfiedTypes = getSatisfiedDocumentTypes('user', [], [doc]);
						return allRequiredIds.some((docTypeId) => docSatisfiedTypes.includes(docTypeId));
					});
		const byId = new Map();
		matchedDocs.forEach((doc) => {
			const id = doc.id ?? doc._id;
			if (id) byId.set(id, doc);
		});
		const existingMatchingDocuments = [...byId.values()];
		onChange({ target: { name: 'existingMatchingDocuments', value: existingMatchingDocuments } });
	}, [requiredDocumentTypes, existingUserDocuments]);

	const skipToUpload = (certs = []) => {
		setAllCertificates(certs);
		onChange({ target: { name: 'userCertificates', value: certs } });
		setCurrentScreen('upload');
	};

	// Handle external source lookup (BeSaCC) - similar to VCA lookup
	const handleExternalLookup = async () => {
		try {
			setCurrentScreen('processing');

			if (!needsBeSaCCLookup()) {
				skipToUpload();
				return;
			}

			if (!input.lastName || !input.dateOfBirth) {
				skipToUpload();
				return;
			}

			// Lookup certificates from BeSaCC using lastName and dateOfBirth (similar to VCA)
			const url = `${apiUrl}/user/${input.lastName}/${format(parseISO(input.dateOfBirth), 'yyyy-MM-dd')}`;
			const { body } = await request.get(url);

			if (!body || !body.length) {
				skipToUpload();
				return;
			}

			// Normalize certs with stable ids for form state (same shape as SafetyCertificatesUserSection)
			const normalizedCerts = (body || []).map((c, i) => ({
				...c,
				id: c.id ?? c.certificateNumber ?? `besacc-${i}`,
				certificateNumber: c.certificateNumber ?? c.id ?? String(i),
				certificateType: c.certificateType ?? c.type ?? '',
				certificateIssuer: c.certificateIssuer ?? '',
			}));

			skipToUpload(normalizedCerts);
		} catch (error) {
			console.error('Error searching for certificates:', error);
			skipToUpload();
		}
	};

	// Same pattern as SafetyCertificatesUserSection; Checkbox passes { target: { name, value } } so use value not checked
	const handleCertificateToggle = (index, selected) => {
		const checked = selected === true || selected === 'true';
		const cert = userCertificates[index];
		if (checked) {
			onChange({
				target: {
					name: 'userCertificates',
					value: [...selectedCertificates, cert],
				},
			});
		} else {
			onChange({
				target: {
					name: 'userCertificates',
					value: selectedCertificates.filter((sc) => getCertKey(sc) !== getCertKey(cert)),
				},
			});
		}
	};

	const handleAnswerChange = ({ target }) => {
		const { name, value } = target;

		// Convert radio button value ('yes'/'no') to boolean for internal logic
		// We only care about 'yes' answers, but store the actual selection
		const booleanValue = value === 'yes' ? true : value === 'no' ? false : undefined;

		onChange({
			target: {
				name: 'answers',
				value: {
					...answers,
					[name]: booleanValue,
				},
			},
		});
	};

	const handleDocumentChange = (newFiles) => {
		// MultipleFileUpload passes the full array of files
		onChange({
			target: {
				name: 'userDocuments',
				value: Array.isArray(newFiles) ? newFiles : [newFiles],
			},
		});
	};

	const handleMissingCertificationsAcknowledgment = (acknowledged) => {
		onChange({
			target: {
				name: 'missingCertificationsAcknowledged',
				value: acknowledged,
			},
		});
	};

	const defaultPage = 'questions';
	const [currentScreen, setCurrentScreen] = useState(defaultPage);

	// Check if all questions have been answered (either yes or no)
	const areAllQuestionsAnswered = () => {
		if (workActivities.length === 0) return true; // No questions means all answered
		return workActivities.every((activity) => {
			const answer = answers[activity.label];
			return answer === true || answer === false; // Must be explicitly yes or no
		});
	};

	// Recalculate canContinue when answers or documents change
	const canContinue = handleCanContinue();

	const screens = {
		questions: {
			next: () => handleExternalLookup(),
			component: QuestionsPage,
			canContinue: areAllQuestionsAnswered(), // Require all questions to be answered
		},
		processing: {
			component: ProcessingPage,
		},
		upload: {
			previous: () => setCurrentScreen('questions'),
			next: (event) => navigationProps.next(event),
			component: UploadPage,
			canContinue: canContinue,
		},
	};

	const screen = screens[currentScreen] || screens.questions;

	const screenProps = {
		input,
		userDocuments,
		existingUserDocuments,
		userCertificates,
		missingCertificationsAcknowledged,
		answers,
		workActivities,
		requiredDocumentTypes,
		onAnswerChange: handleAnswerChange,
		onCertificateToggle: handleCertificateToggle,
		onDocumentChange: handleDocumentChange,
		onMissingCertificationsAcknowledgment: handleMissingCertificationsAcknowledgment,
		text,
		title,
		subtitle,
		uploadUrl,
		disabled,
		useCamera,
		cameraIncludes,
		cameraRotationAngle,
		documentTypesMap,
	};

	return (
		<SectionContainer
			background={background}
			style={style}
			Navigation={
				<SectionNav
					{...navigationProps}
					hasPrevious={!!screen.previous}
					hasNext={currentScreen !== 'upload' || navigationProps.hasNext}
					canContinue={!!screen.canContinue}
					next={screen.next}
					previous={screen.previous}
					selectedLanguage={selectedLanguage}
				/>
			}
		>
			{screen && <screen.component {...screenProps} />}
		</SectionContainer>
	);
};

WorkTypeSafetyCertificatesSection.type = 'WorkTypeSafetyCertificatesSection';
WorkTypeSafetyCertificatesSection.hasNavigation = true;
WorkTypeSafetyCertificatesSection.spec = [
	{
		name: 'WorkTypeSafetyCertificatesSection',
		key: 'WorkTypeSafetyCertificatesSection',
		title: 'Section to collect work-type specific safety certificates',
		allowedFields: ['WorkActivityQuestion'],
		translatable: ['title', 'subtitle', 'navigation.previousLabel', 'navigation.nextLabel', 'navigation.submitLabel'],
		fields: [
			{
				type: 'TextField',
				name: 'title',
				label: 'Section title',
			},
			{
				type: 'TextField',
				name: 'subtitle',
				label: 'Section subtitle',
			},
			{
				type: 'Checkbox',
				name: 'useCamera',
				label: 'Force camera only (kiosk environments only — requires device to be registered)',
			},
		],
		defaultProperties: {
			refDataMap: {
				'userData.userId': 'user.id',
				'userData.firstName': 'user.firstName',
				'userData.lastName': 'user.lastName',
				'userData.dateOfBirth': 'user.userData.dateOfBirth',
			},
			title: 'Work Activity Questions',
			subtitle: 'Please answer the following questions about the type of work you will be performing.',
		},
	},
];

export default WorkTypeSafetyCertificatesSection;
